import type { TranslationTree } from '../../types';

export const toolView: TranslationTree = {
  toolView: {
    list: {
      pageTitle: 'Werkzeugauswahl',
      breadcrumb: {
        tools: 'Werkzeuge',
      },
      actions: {
        refresh: 'Aktualisieren',
        scan: 'Werkzeug scannen',
        expandAll: 'Alle ausklappen',
        collapseAll: 'Alle einklappen',
        showMore: 'Mehr Ergebnisse anzeigen',
      },
      tabs: {
        hierarchy: 'Hierarchieansicht',
        table: 'Tabellenansicht',
      },
      filters: {
        title: 'Filter',
        reset: 'Zurücksetzen',
        activeCount: '{{count}} aktiv',
        search: {
          label: 'Suche',
          placeholder: 'Nach Referenz oder Werkzeugnummer suchen',
        },
        reference: {
          label: 'Referenz',
          all: 'Alle Referenzen',
        },
        health: {
          label: 'Gesundheitsstatus',
          any: 'Alle Status',
        },
      },
      hierarchy: {
        error: 'Werkzeuge konnten nicht geladen werden',
        retry: 'Wiederholen',
        empty: {
          title: 'Keine Werkzeuge verfügbar',
          description: 'Es wurde keine Referenz oder kein Werkzeug im System gefunden.',
        },
        toolCount: '{{count}} Werkzeuge',
        toggleDisplay: 'Anzeige von {{reference}} umschalten',
        openTool: 'Werkzeug {{toolId}} Details öffnen',
        noTrace: 'Noch nicht verwendet',
      },
      table: {
        headers: {
          tool: 'Werkzeug',
          reference: 'Referenz',
          lastTrace: 'Letzte Meldung',
          state: 'Zustand',
        },
        error: 'Werkzeuge konnten nicht geladen werden',
        retry: 'Wiederholen',
        empty: 'Kein Werkzeug entspricht den ausgewählten Filtern.',
        footer: {
          references: '{{count}} Referenz(en)',
          tools: '{{count}} Werkzeug(e)',
          noData: 'Keine Daten verfügbar',
        },
        openTool: 'Werkzeug {{toolId}} Details öffnen',
        noTrace: 'Noch nicht verwendet',
      },
      traceCount: {
        one: 'Eintrag',
        many: 'Einträge',
      },
      pagination: {
        showing: '{{count}} von {{total}} Elementen anzeigen',
        showMore: 'Mehr anzeigen',
      },
    },
    rul: {
      title: 'Verbleibende Restlebensdauer (RUL)',
      comingSoon: {
        title: 'Verbleibende Restlebensdauer — demnächst verfügbar',
        description:
          'Das Predictive-Maintenance-Modell ist noch nicht verbunden. Diese Funktion wird verfügbar sein, sobald der ML-Endpunkt bereit ist.',
      },
    },
    addToolDialog: {
      title: 'Werkzeug registrieren',
      description:
        'Diese Werkzeug-ID wurde nicht in der Datenbank gefunden. Geben Sie die Details ein, um sie jetzt zu registrieren.',
      scannedId: 'Gescannte ID',
      fields: {
        reference: {
          label: 'Interne Referenz',
          placeholder: 'z. B. REF-TC4-Ø12',
        },
        company: {
          label: 'Unternehmensname',
          placeholder: 'z. B. Sandvik',
        },
      },
      actions: {
        add: 'Werkzeug hinzufügen',
        cancel: 'Abbrechen',
      },
      errors: {
        default: 'Werkzeug konnte nicht registriert werden. Bitte erneut versuchen.',
      },
    },
    scanDialog: {
      title: 'Werkzeug scannen',
      description: 'Wählen Sie Ihre bevorzugte Scan-Methode zur Identifizierung des Werkzeugs.',
      helper: 'Wählen Sie oben einen Tab aus, um mit dem Scannen zu beginnen.',
      tabs: {
        camera: 'Kamera',
        keyence: 'Keyence',
        manual: 'Manuell',
      },
      actions: {
        cancel: 'Schließen',
      },
    },
    detail: {
      description: 'Überwachen Sie Zustand, Leistung und aktuelle Vorgänge dieses Werkzeugs.',
      pageTitle: 'Werkzeugdetails',
      loadingSummary: 'Werkzeugübersicht wird geladen',
      actions: {
        showList: 'Detailansicht anzeigen',
        showOverview: 'Zur Übersicht zurückkehren',
        backToList: 'Zurück zur Liste',
      },
      error: {
        invalidToolId: 'Ungültige Werkzeugkennung',
        noToolIdProvided: 'Es wurde keine Werkzeugkennung in der URL angegeben.',
        authenticationRequired: 'Authentifizierung erforderlich',
        initializingConnection: 'Verbindung wird initialisiert…',
      },
    },
    metadata: {
      title: 'Werkzeug-Metadaten',
      fields: {
        id: 'Kennung',
        reference: 'Interne Referenz',
        state: 'Zustand',
        machine: 'Aktivste Maschine',
      },
      fallback: {
        notProvided: 'Nicht angegeben',
      },
    },
    traces: {
      errors: {
        unknown: 'Rückverfolgbarkeit des Werkzeugs konnte nicht geladen werden.',
        noSummary: 'Für dieses Werkzeug wurden keine verwertbaren Meldungen gefunden.',
      },
      table: {
        title: 'Aktuelle Vorgänge',
        description: 'Neueste Rückverfolgbarkeitsmeldungen für dieses Werkzeug.',
        empty: 'Für dieses Werkzeug wurde keine Meldung erfasst.',
        headers: {
          operator: 'Bediener:in',
          duration: 'Dauer',
          pieces: 'Teile',
          date: 'Datum',
          state: 'Zustand',
          operation: 'Operation',
        },
        notProvided: 'k. A.',
      },
    },
    advanced: {
      title: 'Detailliertes Meldungsprotokoll',
      showEmpty: 'Leere Felder anzeigen',
      loading: 'Meldungsdetails werden geladen…',
      noTraces: 'Keine Meldungen für dieses Werkzeug erfasst.',
      errors: {
        noDetails: 'Keine zusätzlichen Details für dieses Werkzeug verfügbar.',
      },
      navigator: {
        prev: 'Vorherige Meldung',
        next: 'Nächste Meldung',
        counter: '{{current}} / {{total}}',
        viewMode: 'Ansicht',
        editMode: 'Bearbeiten',
        modeLabel: 'Anzeigemodus',
      },
      editMode: {
        banner: 'Bearbeitung — nicht gespeicherte Änderungen gehen bei der Navigation verloren',
        save: 'Meldung speichern',
        discard: 'Änderungen verwerfen',
        saving: 'Speichern…',
        saveError: 'Die Meldung konnte nicht gespeichert werden. Bitte erneut versuchen.',
      },
      sections: {
        general: 'Allgemein',
        machine: 'Maschine',
        material: 'Material',
        lubrication: 'Schmierung',
        clamping: 'Spannsystem',
        holder: 'Werkzeughalter',
        breakState: 'Bruch / Zustand',
      },
      detail: {
        machine: 'Maschine',
        machineBrand: 'Maschinenmarke',
        machineModel: 'Maschinenmodell',
        machineSerial: 'Seriennummer',
        machineKafkaName: 'Signalquelle',
        material: 'Material',
        operator: 'Bediener:in',
        pieces: 'Bearbeitete Teile',
        distance: 'Bearbeitete Strecke',
        volume: 'Abgetragenes Volumen',
        malround: 'Rundlaufabweichung',
        pieceDuration: 'Zeit pro Teil (min)',
        totalDuration: 'Gesamtdauer (min)',
        lubBrand: 'Kühlmittelmarke',
        lubType: 'Kühlmitteltyp',
        clampingBrand: 'Spannkraft',
        clampingType: 'Spannart',
        holderBrand: 'Werkzeughalter-Marke',
        holderType: 'Werkzeughalter-Typ',
        operation: 'Vorgang',
        materialPlaceholder: 'Material suchen…',
        materialSearchPlaceholder: 'Suchen…',
        materialNoResults: 'Kein Material gefunden',
        materialLotPlaceholder: 'Materialcharge',
        materialLotSelectFirst: 'Bitte zuerst ein Material auswählen',
        materialLotNoLots: 'Keine Chargen für dieses Material',
        toolState: 'Werkzeugzustand',
        toolNote: 'Notiz',
        changeReasons: 'Wechselgründe',
      },
    },
    dataPanel: {
      title: {
        performance: 'Leistung',
        conditions: 'Einsatzbedingungen',
      },
      perf: {
        totalDuration: 'Gesamtdauer',
        pieces: 'Bearbeitete Teile',
        durationPerPiece: 'Dauer / Teil',
        volume: 'Materialvolumen',
        distance: 'Distanz',
        traces: 'Aufgezeichnete Traces',
      },
      env: {
        machineModel: 'Maschinenmodell',
        operation: 'Operation',
        vc: 'Schnittgeschwindigkeit (vc)',
        fz: 'Vorschub pro Zahn (fz)',
        ap: 'Axiale Tiefe (ap)',
        ae: 'Radiale Tiefe (ae)',
        malround: 'Unrundheit',
        lubType: 'Schmierstofftyp',
        lubBrand: 'Schmierstoffmarke',
        holderType: 'Haltertyp',
        clampingType: 'Spannungstyp',
      },
    },
    family: {
      mill: 'Fräser',
      drill: 'Bohrer',
      turning: 'Drehwerkzeug',
      reamer: 'Reibahle',
      tap: 'Gewindebohrer',
      tool: 'Werkzeug',
    },
  },
};
